import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { TriviaQuestionsProps } from '../components/TriviaQuestions';

import useTrivia from '@/hooks/useTrivia';
import { answerQuestion, getTriviaQuestions } from '@/services/dashboard';
import { ResponsePreguntaTrivia } from '@/types/dashboard';

export const useTriviaQuestions = ({
  triviaData: dataProps,
}: TriviaQuestionsProps) => {
  const { updateTrivia, triviaData } = useTrivia();
  const [answeredQuestionsFlags, setAnsweredQuestionsFlags] = useState<
    boolean[]
  >([]);
  const [currentAnswer, setCurrentAnswer] = useState<number | undefined>(
    undefined
  );
  const [currentQuestion, setCurrentQuestion] =
    useState<ResponsePreguntaTrivia | null>(null);
  const [correctAnswer, setCorrectAnswer] = useState<number>();
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  const { isLoading } = useQuery<ResponsePreguntaTrivia>(
    'data-questions',
    async () => getTriviaQuestions(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setCurrentQuestion(data);
        updateTrivia({
          start: true,
        });
      },
    }
  );

  useEffect(() => {
    if (triviaData.timeOut) {
      setShowTimeoutModal(() => {
        return true;
      });
      updateTrivia({
        stop: true,
        start: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triviaData.timeOut]);

  const isAnswered = (index: number) => {
    const isCurrent = index === (currentQuestion?.nroDePreguntaActual ?? 1) - 1;
    const isAnsweredFlag = answeredQuestionsFlags[index];

    if (isCurrent && isAnsweredFlag === undefined) {
      return 'bg-gdt-blue';
    }
    if (isAnsweredFlag === undefined) {
      return 'bg-gdt-gray-5 lg:bg-gdt-gray-2';
    }
    return isAnsweredFlag ? 'bg-gdt-status-green' : 'bg-gdt-status-red';
  };

  const handleGoToResult = useCallback(() => {
    updateTrivia({
      stop: true,
      start: false,
      goToResult: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnswer = async (answer: number) => {
    if (currentAnswer) {
      return null;
    }

    setCurrentAnswer(answer);

    const getAnswer = await answerQuestion({
      idPregTrivia: currentQuestion?.idPregTrivia ?? 0,
      idRespuesta: answer,
      nroDePreguntaActual: currentQuestion?.nroDePreguntaActual ?? 0,
    });

    if (getAnswer.ordenCorrecta) {
      setCorrectAnswer(getAnswer.ordenCorrecta);
      setAnsweredQuestionsFlags((prev) => [
        ...prev,
        answer === getAnswer.ordenCorrecta,
      ]);
    }
    updateTrivia({
      stop: true,
      start: false,
    });
    if (getAnswer.esLaUltimaPregunta) {
      handleGoToResult();
    }
  };

  const handleNextQuestion = async () => {
    setCurrentAnswer(undefined);

    if (
      currentQuestion?.nroDePreguntaActual === currentQuestion?.totalPreguntas
    ) {
      return handleGoToResult();
    }
    const nextQuestion = await getTriviaQuestions();
    if (nextQuestion.idPregTrivia) {
      setCurrentQuestion(nextQuestion);
    }
    updateTrivia({
      stop: false,
      start: true,
    });
  };

  const handleCloseModal = useCallback(() => {
    setShowTimeoutModal(false);
    handleGoToResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleNextQuestion,
    isLoading,
    correctAnswer,
    currentAnswer,
    currentQuestion,
    handleAnswer,
    isAnswered,
    dataProps,
    closeModal: handleCloseModal,
    showTimeoutModal,
    triviaData: dataProps,
  };
};
